import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { AUDIENCES, MELBOURNE_BADGES } from "@/data/site";

export const Hero = () => {
  const [active, setActive] = useState(AUDIENCES[0].id);
  const current = AUDIENCES.find((a) => a.id === active);

  return (
    <section className="relative overflow-hidden dotgrid">
      <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#FACC15]/30 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 top-32 h-80 w-80 rounded-full bg-[#0E9E96]/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 py-10 lg:px-8 lg:py-14">
        <div className="flex flex-wrap gap-2" data-testid="audience-switcher">
          {AUDIENCES.map((a) => (
            <button
              key={a.id}
              data-testid={`audience-tab-${a.id}`}
              onClick={() => setActive(a.id)}
              className={`rounded-full px-4 py-2 text-sm font-extrabold transition-all ${
                active === a.id
                  ? "bg-[#0E9E96] text-white shadow-md"
                  : "border-2 border-[#CFE3E2] bg-white text-slate-600 hover:border-[#0E9E96]/50"
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
            >
              <div className="mb-4 flex flex-wrap gap-2">
                {MELBOURNE_BADGES.map((b) => (
                  <span
                    key={b}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-extrabold text-[#0B3B38] ring-1 ring-[#0E9E96]/30"
                  >
                    <Check className="h-3.5 w-3.5 text-[#0E9E96]" /> {b}
                  </span>
                ))}
              </div>

              <h1 className="font-head text-4xl font-extrabold leading-[1.05] text-slate-900 sm:text-5xl lg:text-6xl">
                {current.headline}
              </h1>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                {current.blurb}
              </p>

              <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                {current.points.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm font-bold text-slate-700">
                    <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-[#0E9E96]/12 text-[#0E9E96]">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/inquire"
                  data-testid="hero-quote-btn"
                  className="group inline-flex h-12 items-center gap-2 rounded-full bg-[#F97316] px-7 text-base font-extrabold text-white shadow-lg transition-all hover:bg-[#ea6a0c]"
                >
                  {current.cta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/programs"
                  data-testid="hero-programs-btn"
                  className="inline-flex h-12 items-center rounded-full border-2 border-[#0E9E96] px-7 text-base font-extrabold leading-none text-[#0E9E96] transition-colors hover:bg-[#0E9E96]/5"
                >
                  Explore Sports Programs
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>

          <motion.div
            key={active + "-card"}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="relative"
          >
            <div className="rounded-[2rem] border border-[#CFE3E2] bg-white p-7 shadow-xl">
              <div className="rounded-2xl bg-gradient-to-br from-[#0E9E96] to-[#0B6F6A] p-6 text-white">
                <p className="text-xs font-extrabold uppercase tracking-wider text-teal-100">
                  SplayLab session
                </p>
                <p className="mt-1 font-head text-2xl font-extrabold">
                  A 30–45 min movement lab
                </p>
                <p className="mt-2 text-sm text-teal-50/85">
                  Coaches, equipment and a full neuro-physical program — delivered across Melbourne, set up for you.
                </p>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  { k: "Ages", v: "ELC – Year 2" },
                  { k: "You bring", v: "Just the kids" },
                  { k: "When", v: "Anytime" },
                  { k: "Response", v: "Within 4 hrs" },
                ].map((s) => (
                  <div key={s.k} className="rounded-xl bg-[#F1F7F7] p-3">
                    <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">{s.k}</p>
                    <p className="font-head text-lg font-extrabold text-slate-800">{s.v}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/inquire"
                className="mt-5 flex h-11 items-center justify-center rounded-full bg-[#0E9E96] text-sm font-extrabold text-white transition-colors hover:bg-[#0b847d]"
              >
                Inquire for Program Pricing
              </Link>
            </div>
            <div className="absolute -bottom-4 -left-3 rounded-2xl border border-[#FACC15]/50 bg-[#FACC15] px-4 py-2 shadow-lg">
              <p className="font-head text-sm font-extrabold text-[#5A4300]">Custom quote in 4 hrs</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
